/**
 * React hook for real-time experience synchronization.
 *
 * Provides a convenient interface for syncing ephemeral UI state between dealer and customer
 * without involving session state or database writes.
 *
 * @example
 * ```typescript
 * // In a React component
 * const { state, send, isConnected } = useExperienceSync<IExperienceSyncState>({
 *   sessionId,
 *   initialState: { sliderPercentage: 50, sliderEnabled: false, swapEnabled: false },
 *   schema: experienceSyncSchema,
 *   isHost: user.type === 'dealer',
 * });
 *
 * // Render based on synced state
 * <Slider value={state.sliderPercentage} onChange={(v) => send({ sliderPercentage: v })} />
 * ```
 */

import * as React from 'react';
import { z } from 'zod';
import { createExperienceSync, IExperienceSyncChannel } from './experienceSync';
import logger from 'utils/log';

export interface IUseExperienceSyncOptions<T extends Record<string, unknown>> {
  /** Session ID to scope the sync channel */
  sessionId: string | undefined;
  /** Topic ID for the sync channel (defaults to 'experience') */
  topicId?: string;
  /** Initial state */
  initialState: T;
  /** Zod schema for validation */
  schema?: z.ZodType<T>;
  /** Whether this client is the host (dealer) that manages state */
  isHost?: boolean;
  /** Auto-connect on mount (defaults to true) */
  autoConnect?: boolean;
  /** Callback when state changes */
  onStateChange?: (state: T) => void;
}

export interface IUseExperienceSyncReturn<T> {
  /** Current synced state */
  state: T;
  /** Send partial state update to peers */
  send: (data: Partial<T>) => void;
  /** Whether connected to the sync channel */
  isConnected: boolean;
  /** Connection ID */
  connectionId: string | undefined;
  /** Manually connect to sync channel */
  connect: () => Promise<string | undefined>;
  /** Manually disconnect from sync channel */
  disconnect: () => void;
  /** Update local state without sending to peers */
  setLocalState: React.Dispatch<React.SetStateAction<T>>;
}

/**
 * React hook for real-time experience synchronization.
 */
export function useExperienceSync<T extends Record<string, unknown>>(
  options: IUseExperienceSyncOptions<T>
): IUseExperienceSyncReturn<T> {
  const {
    sessionId,
    topicId = 'experience',
    initialState,
    schema,
    isHost = false,
    autoConnect = true,
    onStateChange,
  } = options;

  const [state, setState] = React.useState<T>(initialState);
  const [isConnected, setIsConnected] = React.useState(false);
  const [connectionId, setConnectionId] = React.useState<string | undefined>();

  // Keep a ref to the sync instance to avoid recreating on every render
  const syncRef = React.useRef<IExperienceSyncChannel<T> | null>(null);
  // Track the latest state for snapshot responses
  const stateRef = React.useRef<T>(state);
  stateRef.current = state;

  // Memoize callbacks to prevent unnecessary effect triggers
  const onStateChangeRef = React.useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  // Create or update sync instance when sessionId changes
  React.useEffect(() => {
    if (!sessionId) {
      // No session, clean up existing connection
      if (syncRef.current) {
        syncRef.current.disconnect();
        syncRef.current = null;
        setIsConnected(false);
        setConnectionId(undefined);
      }
      return;
    }

    // Create new sync instance
    const sync = createExperienceSync<T>({
      sessionId,
      topicId,
      schema,
      onConnect: (id) => {
        setConnectionId(id);
        setIsConnected(true);
        logger.log(`[useExperienceSync] Connected: ${id}`);
      },
      onDisconnect: () => {
        setConnectionId(undefined);
        setIsConnected(false);
        logger.log('[useExperienceSync] Disconnected');
      },
      onError: (error) => {
        logger.error('[useExperienceSync] Error:', error);
      },
    });

    // Set up event listeners
    // Every writer in this hook keeps `stateRef` authoritative, and none of them use a
    // functional updater (AVS-4401). A functional updater only reaches the ref at the next
    // render, so a local `send()` landing in that window would compose off pre-remote values —
    // and because sends now carry the FULL state, those stale fields would go out on the wire
    // and revert the peer that just sent them. Composing off the ref closes that window.
    sync.on('sync', (data) => {
      const newState = { ...stateRef.current, ...data };
      stateRef.current = newState;
      setState(newState);
      onStateChangeRef.current?.(newState);
    });

    sync.on('snapshot', (data) => {
      stateRef.current = data as T;
      setState(data as T);
      onStateChangeRef.current?.(data as T);
    });

    // Host responds to snapshot requests
    if (isHost) {
      sync.on('snapshotRequest', (from) => {
        logger.log(`[useExperienceSync] Snapshot requested by: ${from}`);
        sync.sendSnapshot(stateRef.current);
      });

      // Host proactively sends snapshot when a new peer connects
      sync.on('peerConnected', (peerConnectionId) => {
        logger.log(`[useExperienceSync] New peer connected: ${peerConnectionId}, sending snapshot`);
        // Small delay to ensure the new peer has finished setting up listeners
        setTimeout(() => {
          sync.sendSnapshot(stateRef.current);
        }, 150);
      });

      // Host re-asserts its own view state when ITS channel (re)connects (AVS-4382).
      // `peerConnected` above covers a peer joining; this covers the host's own
      // reconnect, which has no peer-announcement equivalent and would otherwise
      // strand any state change made during the disconnected gap (`send` drops
      // while disconnected, see useExperienceSync.ts's `send` and
      // experienceSync.ts's `send`). Registered on the channel's `connect` event
      // (not the `onConnect` constructor option) so it can safely reference `sync`
      // without a self-reference hazard inside the initializer.
      sync.on('connect', () => {
        sync.sendSnapshot(stateRef.current);
      });
    }

    syncRef.current = sync;

    // Auto-connect if enabled
    if (autoConnect) {
      sync.connect().catch((error) => {
        logger.error('[useExperienceSync] Failed to connect:', error);
      });
    }

    // Cleanup on unmount or sessionId change
    return () => {
      sync.disconnect();
      syncRef.current = null;
    };
  }, [sessionId, topicId, schema, isHost, autoConnect]);

  // Request snapshot when customer connects (non-host)
  React.useEffect(() => {
    if (isConnected && !isHost && syncRef.current) {
      // Small delay to ensure host has set up listeners
      const timeoutId = setTimeout(() => {
        logger.log('[useExperienceSync] Requesting snapshot from host');
        syncRef.current?.requestSnapshot();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isConnected, isHost]);

  const send = React.useCallback((data: Partial<T>) => {
    // Compose against the ref rather than the rendered state (AVS-4401). Two sends in one tick
    // both read the same render, so merging off `prev` alone let the second one revert the
    // first. Updating the ref here keeps consecutive sends composable, and keeps the side
    // effect out of the setState updater, which React may invoke twice.
    const nextState = { ...stateRef.current, ...data };
    stateRef.current = nextState;

    // Always update local state so UI responds immediately even without a session
    setState(nextState);
    onStateChangeRef.current?.(nextState);

    // Send to peers only when connected
    if (!syncRef.current?.isConnected()) {
      return;
    }
    // Send the FULL state, not just the delta (AVS-4401). The relay fans out to whoever is
    // connected at that instant and never replays, and a peer that missed a message has no way
    // to detect the gap. Sending the whole (small) state makes every message self-healing: the
    // next one silently repairs the miss instead of leaving the customer stale until the dealer
    // happens to act again.
    syncRef.current.send(nextState);
  }, []);

  const connect = React.useCallback(async () => {
    if (!syncRef.current) {
      logger.warn('[useExperienceSync] Cannot connect: no session');
      return undefined;
    }
    return syncRef.current.connect();
  }, []);

  const disconnect = React.useCallback(() => {
    syncRef.current?.disconnect();
  }, []);

  /**
   * Local-only state update. Wrapped rather than handing out `setState` directly so it keeps
   * `stateRef` authoritative like every other writer (AVS-4401) — a functional update through
   * the raw setter would not, and the next `send()` would then broadcast the pre-update values.
   */
  const setLocalState = React.useCallback<React.Dispatch<React.SetStateAction<T>>>((action) => {
    const next = typeof action === 'function' ? (action as (prev: T) => T)(stateRef.current) : action;
    stateRef.current = next;
    setState(next);
  }, []);

  return {
    state,
    send,
    isConnected,
    connectionId,
    connect,
    disconnect,
    setLocalState,
  };
}
